import { sortBy, uniqBy } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';
import { v4 as uuidV4 } from 'uuid';

import {
  EmployeeCalendarEvent,
  EmployeeCalendarPersonWithLegalEntityId,
  getEmployeeCalendarPersonsFromEvents,
} from '~/components/EmployeeCalendar';
import {
  useEmployeeCalendarPublicHolidays,
  usePublicAbsenceCalendarConfig,
} from '~/components/EmployeeCalendar/Absences';
import { useAllEmployees } from '~/data/employees';
import { VacationHistory, VacationsReportData } from '~/data/reports';
import { useShowEmployeesModeSelector } from '~/scenes/PortalModules/Analytics/People/AbsenceCalendarReport/ShowEmployeesModeSelector/useShowEmployeesModeSelector';

type PropsType = {
  startDate: Date;
  endDate: Date;
  data: VacationsReportData[];
};

export const useVacationsCalendarReport = (props: PropsType) => {
  const { data, startDate, endDate } = props;

  const { getEmployeeById } = useAllEmployees();

  // Process absences
  const {
    absenceEntries,
    reportEmployees,
    absenceEmployees,
    hasNonAbsentEmployees,
    hasAbsentEmployees,
  } = useMemo(() => {
    const reportEmployees: EmployeeCalendarPersonWithLegalEntityId[] = [];

    const absenceEntries: EmployeeCalendarEvent[] = data
      .map((reportData) => {
        const {
          fEmpVacationDetails,
          fEmpId,
          fEmpFirstName,
          fEmpLastName,
          fEmpLegalEntityId,
        } = reportData;

        const employeeData = getEmployeeById(fEmpId);

        reportEmployees.push({
          employeeId: fEmpId,
          firstName: fEmpFirstName,
          lastName: fEmpLastName,
          profilePic: employeeData?.fEmpProfPictureId,
          legalEntityId: fEmpLegalEntityId,
        });

        const getEvent = (absence: VacationHistory): EmployeeCalendarEvent => {
          const {
            fEmpVacationReason,
            fEmpVacationEndDate,
            fEmpVacationStartDate,
          } = absence;

          return {
            uId: uuidV4(),
            employeeId: fEmpId,
            firstName: fEmpFirstName,
            lastName: fEmpLastName,
            profilePic: employeeData?.fEmpProfPictureId,
            eventCategory: '',
            eventCode: fEmpVacationReason,
            status: '',
            startDate: moment(fEmpVacationStartDate).toDate(),
            endDate: !!fEmpVacationEndDate
              ? moment(fEmpVacationEndDate).toDate()
              : undefined,
          };
        };

        return fEmpVacationDetails.map(getEvent);
      })
      .flat();

    const sortedReportEmployees = sortBy(
      uniqBy(reportEmployees, (emp) => emp.employeeId),
      (emp) => emp.firstName,
    );

    const absenceEmployees =
      getEmployeeCalendarPersonsFromEvents(absenceEntries);

    const hasNonAbsentEmployees =
      reportEmployees.length > absenceEmployees.length;
    const hasAbsentEmployees = !!absenceEmployees.length;

    return {
      reportEmployees: sortedReportEmployees,
      absenceEntries,
      absenceEmployees,
      hasNonAbsentEmployees,
      hasAbsentEmployees,
    };
  }, [data, getEmployeeById]);

  const { publicHolidayEvents, isLoading: isLoadingPublicHolidays } =
    useEmployeeCalendarPublicHolidays({
      startDate,
      endDate,
      reportEmployees,
    });

  const { allEvents } = useMemo(() => {
    const allEvents = publicHolidayEvents.concat(absenceEntries);

    return {
      allEvents,
    };
  }, [absenceEntries, publicHolidayEvents]);

  const calendarProps = usePublicAbsenceCalendarConfig({
    events: allEvents,
  });

  const showEmployeesModeSelector = useShowEmployeesModeSelector({
    hasAbsentEmployees,
    hasNonAbsentEmployees,
  });

  return {
    counts: {
      employees: reportEmployees.length,
      absences: absenceEntries.length,
    },
    showEmployeesModeSelector,
    calendarProps: {
      ...calendarProps,
      listEmployees:
        showEmployeesModeSelector.value === 'ALL'
          ? reportEmployees
          : absenceEmployees,
    },
    isLoading: isLoadingPublicHolidays,
  };
};
